import { DateService, DateUnit } from "@app/services/date.service";
import { IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { DateRange } from "@enums/DateRange.enum";
import { ReasonCode } from "@enums/ReasonCode";
import { SortType } from "@enums/SortType";
import { VehicleType } from "@enums/VehicleType.enum";
import { FoundationPeripheralService } from "@foundation/_services/peripheral.service.ajs";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { IDriverFilter } from "@interfaces/filters/IDriverFilter";
import { IFilterOptions } from "@interfaces/filters/IFilterOptions";
import { IGroupFilter } from "@interfaces/filters/IGroupFilter";
import { IVehicleFilter } from "@interfaces/filters/IVehicleFilter";
import { IDriver } from "@interfaces/IDriver";
import { IGoogleMapsMarker, IGoogleMapsMarkerOptions } from "@interfaces/IGoogleMapsMarkerOptions";
import { IVehicle } from "@interfaces/IVehicle";
import { IDataDefinition } from "@reports/models/IReportDefinition";
import { AccelerometerRecordService, IAccelerometerRecord, Severity } from "@services/accelerometerRecord.service";
import { AccountFeatureService } from "@services/accountFeature.service";
import { CanvasService, IPieDataSeries } from "@services/canvas.service";
import { ColourService } from "@services/colour.service";
import { DriverService } from "@services/driver.service";
import { EventService, IEvent, IEventCategory } from "@services/event.services";
import { FilterOptionsService } from "@services/filterOptions.service";
import { GoogleMarkerService } from "@services/googleMarker.service";
import { MapService } from "@services/map.service";
import { MessageDialogService } from "@services/messageDialog.service";
import { PlotService } from "@services/plot.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import { VeriMarkerService } from "@services/veriMarker.service";
import { ClusterIcon, IMarkerOptions, IPolyline } from "@typings/google.maps.angular";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as moment from "moment";
import { Subscription } from "rxjs";
import { AccountFeatures } from "../../../../enum/account/AccountFeature";
import { CoordsModel } from "../../livemap/model/CoordsModel";
import { CameraDisplayService } from "../../videos/cameraDisplay.service";
import { VideoStatus } from "../../videos/enums/VideoStatus";
import { IVideoEntry } from "../../videos/models/IVideoEntry";
import { CameraFootageService } from "../../videos/services/camera-footage.service";
import { CameraStatusService } from "../../videos/services/camera-status.service";
import { VideoEntryService } from "../../videos/services/video-entry.service";
import { VideoReviewService } from "../../videos/services/video-review.service";
import { VideoEntryUtils } from "../../videos/utils/video-entry-utils";

import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { NavigationService } from "@app/services/navigation.service";
import { AccountSettingsService, IAccountSettingsContent } from "@services/account-settings.service";
import { ExploreNavigationModule } from "../insights.navigation";
import { IEventBar, IEventBarSegment } from "./components/fleetEventBar/fleetEventBar.component";
import "./fleetEvents.component.scss";
import { ILocationInformation } from "@app/interfaces/ILocationInformation";

interface IComponentBindings {
    filteredDriverId?: number;
}

interface IFleetDriverEvent {
    repeatId?: number;
    driver: IDriver;
    selected?: boolean;
    events?: IEvent[];
    total?: number;
    overviewBar?: IEventBar;
    isUnknownDriver?: boolean;
}

interface IFleetLocationEvent {
    repeatId?: number;
    locationIdentifier: string;
    selected?: boolean;
    postcode?: string;
    events?: IEvent[];
    total?: number;
    overviewBar?: IEventBar;
}

interface IFleetEventFiltersSelected extends IFilterOptions {
    defaultDateRange?: number;
    eventCategory?: IEventCategory;
    driverEvent?: IFleetDriverEvent;
    locationEvent?: IFleetLocationEvent;
}

interface ICityPostcode {
    postcode?: string;
    city?: string;
}

class FleetEventsComponentController implements angular.IComponentController, IComponentBindings {
    private unseenVideoCountUpdatedSub = Subscription.EMPTY;


    userHasVideoAccess: boolean;
    filteredDriverId?: number; // currently un-used - optional driver id to view component with filtered view for specific driver
    reportDefinition?: IDataDefinition;
    currentMainTabIndex: number = -1;
    currentSelectedEventTabIndex: number = -1;

    // spinners
    isLoadingData: boolean = true;

    // raw data
    initialLoadComplete: boolean = false;
    drivers: IDriver[] = [];
    vehicles: IVehicle[] = [];
    accelerometerEventsMap: Map<number, IAccelerometerRecord>;
    events: IEvent[] = [];

    // filtered data for the map
    displayedEvents: IEvent[] = [];

    // main map
    currentVeriMap: IVeriMapComponent;
    selectedMapEventPolylines: IPolyline[] = [];
    selectedMapEvent: IEvent = null;
    hoveredMapEvent: IEvent = null;
    eventMarkerOptions: IMarkerOptions<IEvent>;

    // mini-map
    currentMiniVeriMap: IVeriMapComponent;
    selectedEventMapMarkerStart: IGoogleMapsMarker;
    selectedEventMapMarkerEnd: IGoogleMapsMarker;
    mapCenterFromSelectedEvent: CoordsModel;

    // selected-event information panel
    loadingSelectedEventPlots: boolean;
    eventsRelatedToSelectedMapEvent: IEvent[] = [];

    // selected-event videos
    loadingSelectedEventVideos: boolean = false;
    loadingSelectedEventVideoChannels: boolean = false;
    selectedMapEventVideoRequested: boolean = false;
    selectedEventVehicleHasRequestFootageFunctionality: boolean = false;
    selectedEventVideoDownloadProgress: number = -1;
    selectedEventVideoDownloadStatus: string;
    selectedEventVideos: any[] = [];
    requestedVideoEventIds: number[] = [];
    gatherEventVideosInterval: angular.IPromise<any>;

    // processed data
    eventCategories: IEventCategory[] = [];
    driverEvents: IFleetDriverEvent[] = [];
    locationEvents: IFleetLocationEvent[] = [];

    // overview bars
    contextualSpeedingRoadSpeedOverviewBars: IEventBar[];
    contextualSpeedingRoadTypeOverviewBars: IEventBar[];
    contextualSpeedingVehicleTypeOverviewBars: IEventBar[];
    driverEventsOverviewBars: IEventBar[];
    locationEventsOverviewBars: IEventBar[];

    // filters
    filters: IFleetEventFiltersSelected = {
        dateFilter: null,
        groupFilter: null,
        defaultDateRange: DateRange.Today,
        eventCategory: null,
        driverEvent: null,
        locationEvent: null
    };

    // dataview
    vTableColumns: IVTableColumn<IEvent>[];
    vTableRows: IVTableRow[];

    severity = Severity;

    accountSettings: IAccountSettingsContent;


    get isContextualSpeedingEvent(): boolean {

        return !!this.filters.eventCategory && this.filters.eventCategory.name === this.translationService.tEnum(ReasonCode, "ReasonCode", ReasonCode.ContextualSpeeding);

    }

    static $inject = ["$q", "$interval", "$timeout", "foundationPeripheralService", "cameraDisplayService", "plotService", "veriDate", "colourService", "canvasService", "driverService", "eventService", "accelerometerRecordService", "messageDialogService", "vehicleService", "veriMarkerService", "googleMarkerService", "mapService", "unitConversionService", "accountFeatureService", "translationService", "filterOptionsService", "videoReviewService", "videoEntryService", "dateService", "accountSettingsService", "cameraFootageService", "cameraStatusService", "navigationService", "vTableService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $interval: angular.IIntervalService,
        private readonly $timeout: angular.ITimeoutService,
        private readonly foundationPeripheralService: FoundationPeripheralService,
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly plotService: PlotService,
        private readonly veriDate: VeriDate,
        private readonly colourService: ColourService,
        private readonly canvasService: CanvasService,
        private readonly driverService: DriverService,
        private readonly eventService: EventService,
        private readonly accelerometerRecordService: AccelerometerRecordService,
        private readonly messageDialogService: MessageDialogService,
        private readonly vehicleService: VehicleService,
        private readonly veriMarkerService: VeriMarkerService,
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly mapService: MapService,
        private readonly unitConversionService: UnitConversionService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly translationService: TranslationService,
        private readonly filterOptionsService: FilterOptionsService,
        private readonly videoReviewService: VideoReviewService,
        private readonly videoEntryService: VideoEntryService,
        private readonly dateService: DateService,
        private readonly accountSettingsService: AccountSettingsService,
        private readonly cameraFootageService: CameraFootageService,
        private readonly cameraStatusService: CameraStatusService,
        private readonly navigationService: NavigationService,
        private readonly vTableService: VTableService) {
    }

    $onInit(): void {

        this.createTable();
        this.userHasVideoAccess = this.accountFeatureService.currentUserHasFeatureAccess(AccountFeatures.Camera);

        this.configureDataViewTable();
        this.configureEventMarkerOptions();

        this.unseenVideoCountUpdatedSub = this.videoReviewService.unseenVideoCountUpdated$.subscribe(() => {
            this.getVideosForEvent(this.selectedMapEvent);
        })

        this.accountSettingsService.getAccountSettings().then(settings => {
            this.accountSettings = settings;
        });
    }

    get accountSettingsShowNonEventFootage(): boolean {
        return !this.accountSettings?.DisableNonEventDownloads?.Value;
    }

    $onChanges() {
        if (this.reportDefinition) {
            this.filters = this.filterOptionsService.createFilterOptions(this.reportDefinition);
        }

        this.getFleetEventsMapData().then(() => {
            this.prepareDefaultView();
            this.initialLoadComplete = true;
        });
    }

    $onDestroy(): void {
        this.unseenVideoCountUpdatedSub?.unsubscribe();
        this.clearData();
    }

    requestVideoForSelectedEvent($event: MouseEvent) {
        if (!this.selectedMapEvent
            || !this.selectedMapEvent?.vehicle
            || !this.selectedMapEvent?.Id
            || !this.selectedMapEvent?.StartTime
            || this.requestedVideoEventIds.indexOf(this.selectedMapEvent.Id) > -1
            || this.eventVideoHasBeenRequested(this.selectedMapEvent)) {
            return;
        }

        const startTime: string = this.dateService.addOrSubtractTime(this.selectedMapEvent.StartTime, -20, DateUnit.Sec).toISOString();

        this.navigationService.go(`${ExploreNavigationModule.FleetEvents}.view`, { vehicleId: this.selectedMapEvent.VehicleId, tab: 'requestVideo', requestVideoTime: startTime })
    }

    playSelectedEventVideo(video: IVideoEntry, $event: MouseEvent) {
        this.cameraDisplayService.showVideoPlayer(video, $event);
    }

    onMarkerClick(marker: google.maps.Marker, eventName: string, model: any, mouseEvent: MouseEvent): void {
        const clickedEvent: IEvent = this.displayedEvents.find(displayedEvent => displayedEvent.Id === model.id);

        if (clickedEvent) {
            this.selectMapEvent(clickedEvent);
        }
    }

    onMarkerMouseover(marker: google.maps.Marker, eventName: string, model: any, mouseEvent: MouseEvent): void {
        this.hoveredMapEvent = this.displayedEvents.find(displayedEvent => displayedEvent.Id === model.id);
    }

    selectMapEvent(event: IEvent): void {
        this.currentSelectedEventTabIndex = 0;
        this.clearCategoryFilterToMatchEvent(event);
        this.selectedMapEvent = event;

        this.processSelectedEventInformation().then(() => {
            this.refreshAllMaps();
            this.checkForEventVideos();
        });
    }

    selectEventsCategory(newEventCategory: IEventCategory): void {
        if (this.eventCategories?.indexOf(newEventCategory) === 0) {
            this.clearEventsCategoryFilter()
            return;
        }

        if (this.eventCategories.find(eventCategory => eventCategory === newEventCategory)) {
            this.filters.eventCategory = newEventCategory;
            this.processDisplayedView();

        }
    }

    selectDriverEvent(driverEvent: IFleetDriverEvent): void {
        if (this.driverEvents?.indexOf(driverEvent) === 0) {
            this.clearDriverEventFilter()
            return;
        }

        this.filters.driverEvent = driverEvent;
        this.processDisplayedView();
    }

    selectLocationEvent(locationEvent: IFleetLocationEvent): void {
        if (this.locationEvents?.indexOf(locationEvent) === 0) {
            this.clearLocationEventFilter()
            return;
        }

        if (!locationEvent?.locationIdentifier) {
            return;
        }

        this.filters.locationEvent = locationEvent;
        this.processDisplayedView();
    }

    clearCurrentSelectedEvent(): void {
        this.selectedMapEventPolylines = [];
        this.selectedMapEvent = null;
        this.selectedEventMapMarkerStart = null;
        this.selectedEventMapMarkerEnd = null;

        this.refreshAllMaps();
        this.cancelExistingEventVideoInterval();
    }

    clearEventsCategoryFilter(): void {
        this.filters.eventCategory = null;
        this.processDisplayedView();
    }

    clearCategoryFilterToMatchEvent(event: IEvent): void {
        if (event && this.selectedMapEvent && event?.eventCategory !== this.selectedMapEvent?.eventCategory) {
            this.clearEventsCategoryFilter();
        }
    }

    clearDriverEventFilter(): void {
        this.filters.driverEvent = null;
        this.processDisplayedView();
    }

    clearLocationEventFilter(): void {
        this.filters.locationEvent = null;
        this.processDisplayedView();
    }

    clearAllFilters(): void {
        this.filters.eventCategory = null;
        this.filters.driverEvent = null;
        this.filters.locationEvent = null;
        this.processDisplayedView();
    }

    onDateFilterChanged(filter: IDateFilter): void {
        this.filters.dateFilter = filter;

        if (this.initialLoadComplete) {
            this.getFleetEventsMapData().then(() => {
                this.prepareDefaultView();
            });
        }
    }

    onGroupFilterChanged(filter: IGroupFilter) {
        this.filters.groupFilter = filter;

        if (this.initialLoadComplete) {
            this.getFleetEventsMapData().then(() => {
                this.prepareDefaultView();
            });
        }
    }

    onEventInformationTabChanged(tabIndex: number, tabTitle: string): void {
        this.currentSelectedEventTabIndex = tabIndex;
    }

    onMainTabChanged(tabIndex: number, tabTitle: string): void {
        this.currentMainTabIndex = tabIndex;
        this.clearCurrentVeriMap();
        this.clearCurrentMiniVeriMap();
    }

    onVeriMapLoaded(veriMap: IVeriMapComponent): void {
        this.currentVeriMap = veriMap;
        this.refreshAllMaps();
    }

    onVeriMiniMapLoaded(veriMap: IVeriMapComponent): void {
        this.currentMiniVeriMap = veriMap;
        this.refreshAllMaps();
    }

    private refreshAllMaps(): void {
        if (this.selectedMapEvent
            && this.selectedMapEvent?.StartLatitude
            && this.selectedMapEvent?.StartLongitude
            && this.selectedMapEvent?.EndLatitude
            && this.selectedMapEvent?.EndLongitude) {
            this.selectedEventMapMarkerStart = this.createMapMarkerForEvent(
                this.selectedMapEvent,
                this.selectedMapEvent.StartLatitude,
                this.selectedMapEvent.StartLongitude,
                this.selectedMapEvent?.eventCategory?.name,
                "warning");

            if (this.selectedMapEvent?.Type == ReasonCode.ContextualSpeeding && this.selectedMapEvent?.contextualSpeedingData?.EncodedPolyline) {
                const contextualSpeedingLinePoints: number[][] = this.mapService.getPointsFromEncodedString(this.selectedMapEvent.contextualSpeedingData.EncodedPolyline);

                // if this contextual speeding event actually has a polyline, create an end flag - don't otherwise, to prevent stacked markers
                if (contextualSpeedingLinePoints?.length > 0) {
                    this.selectedEventMapMarkerEnd = this.createMapMarkerForEvent(
                        this.selectedMapEvent,
                        this.selectedMapEvent.EndLatitude,
                        this.selectedMapEvent.EndLongitude,
                        "End",
                        "flag");
                }
            }

            this.selectedMapEventPolylines = this.createPolylinesForEvent(this.selectedMapEvent);
            this.mapCenterFromSelectedEvent = new CoordsModel(this.selectedEventMapMarkerStart.options.position.lat, this.selectedEventMapMarkerStart.options.position.lng);
        }

        this.refreshMap(this.currentVeriMap);
        this.$timeout(() => {
            this.refreshMap(this.currentMiniVeriMap);
        }, 300);
    }

    private refreshMap(veriMap: IVeriMapComponent): void {
        if (!veriMap) {
            return;
        }

        if (this.selectedMapEvent) {
            this.fitMapToMapEvent(veriMap, this.selectedMapEvent, false);
        }
        else {
            this.fitMapToDisplayedMapEvents(veriMap, this.displayedEvents);
        }
    }

    private getFleetEventsMapData(): angular.IPromise<any> {
        this.isLoadingData = true;
        this.clearCurrentSelectedEvent();

        return this.refreshEventsData().then(() => {
            this.processData();
        }).finally(() => {
            this.isLoadingData = false;
        });
    }

    private getVideosForEvent(event: IEvent): angular.IPromise<void> {
        if (!event || !this.userHasVideoAccess) {
            return;
        }

        this.loadingSelectedEventVideos = true;
        const startTime = this.dateService.addOrSubtractTime(event.StartTime, -20, DateUnit.Sec);
        const endTime = this.dateService.addOrSubtractTime(event.EndTime, 20, DateUnit.Sec);

        const driverFilter: IDriverFilter = {
            driver: event?.driver
        };

        const vehicleFilter: IVehicleFilter = {
            vehicle: event?.vehicle
        }

        const promise = this.videoEntryService.getVideosEntriesWithMappings(
            startTime,
            endTime,
            { vehicleFilter: vehicleFilter, driverFilter: driverFilter }
        ) as angular.IPromise<IVideoEntry[]>;

        return promise.then(videos => {
            if (videos?.length > 0) {
                this.processEventVideos(videos);
            }

            const videosPendingDownload: IVideoEntry[] = this.selectedEventVideos.filter(eventVideo => {
                if (VideoEntryUtils.isDownloading(eventVideo)) {
                    return eventVideo;
                }
            });

            if (videosPendingDownload?.length > 0) {
                videosPendingDownload.forEach(video => {
                    video.vehicle.videoChannels = this.selectedMapEvent.vehicle.videoChannels;
                    this.selectedEventVideoDownloadProgress = VideoEntryUtils.getVideoDownloadProgress(video);
                    this.selectedEventVideoDownloadStatus = this.cameraStatusService.getVideoRequestStatus(video);
                });
            }
            else {
                this.clearSelectedEventVideoDownloadProgress();
            }

            this.loadingSelectedEventVideos = false;
        });
    }

    private processEventVideos(videos: IVideoEntry[]): void {
        if (videos?.length > 0) {
            this.selectedEventVideos = videos.filter(video => this.shouldShowVideoEntry(video));
        }
    }

    private shouldShowVideoEntry(videoEntry: IVideoEntry): boolean {
        return VideoEntryUtils.isDownloading(videoEntry) || videoEntry.Status == VideoStatus.Downloaded;
    }

    private getVideosChannelsForEvent(event: IEvent): angular.IPromise<void> {
        if (!event || !event.VehicleId || !event.vehicle) {
            return;
        }

        this.loadingSelectedEventVideoChannels = true;
        event.vehicle.videoChannels = [];

        return this.cameraStatusService.getVideoChannels(event.VehicleId).then(channels => {
            event.vehicle.videoChannels = channels;
            this.loadingSelectedEventVideoChannels = false;
        }) as angular.IPromise<void>;
    }

    private getRequestFootageFunctionalityAbilityFromEvent(event: IEvent): angular.IPromise<void> {
        if (!event || !event.VehicleId) {
            return;
        }

        this.selectedEventVehicleHasRequestFootageFunctionality = false;

        return this.foundationPeripheralService.getPeripherals(event.VehicleId).then(peripherals => {
            this.selectedEventVehicleHasRequestFootageFunctionality = this.cameraFootageService.isFootageFunctionalityAvailable(peripherals);
        });
    }

    private clearCurrentVeriMap(): void {
        this.currentVeriMap = null;
    }

    private clearCurrentMiniVeriMap(): void {
        this.currentMiniVeriMap = null;
    }

    private clearData(): void {
        this.drivers = [];
        this.vehicles = [];
        this.events = [];
        this.driverEvents = [];
        this.locationEvents = [];
        this.displayedEvents = [];
        this.selectedEventVideos = [];
        this.requestedVideoEventIds = [];

        this.cancelExistingEventVideoInterval();
        this.clearSelectedEventVideoDownloadProgress();
    }

    private refreshEventsData(): angular.IPromise<any> {
        this.clearData();

        const dateFilterStart = (this.filters.dateFilter && this.filters.dateFilter.start) ? moment(this.filters.dateFilter.start) : moment().startOf("d");
        const dateFilterEnd = (this.filters.dateFilter && this.filters.dateFilter.end) ? moment(this.filters.dateFilter.end) : moment().endOf("d");

        const driversPromise = this.driverService.getDrivers();
        const vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });
        const eventsPromise = this.eventService.getEventsForFleet(dateFilterStart, dateFilterEnd);

        const accelerometerEventsPromise = this.accelerometerRecordService.getEventsForFleet({ Start: dateFilterStart.toDate(), End: dateFilterEnd.toDate() });

        return this.$q.all([driversPromise, vehiclesPromise, eventsPromise, accelerometerEventsPromise])
            .then(([drivers, vehicles, events, accelerometerRecords]) => {
                this.drivers = drivers;
                this.vehicles = vehicles;
                this.events = events;
                this.accelerometerEventsMap = new Map(accelerometerRecords.map(record => [record.PlotId, record]));

                // Simple way to filter by vehicle - likely want new calls in future?
                if (this.filters.vehicleFilter?.vehicle?.Id) {
                    this.events = this.events.filter(e => e.VehicleId == this.filters.vehicleFilter.vehicle.Id);
                }
            })
            .catch(err => {
                this.messageDialogService.handleError(err);
            });
    }

    private updateSelectedEventPlots(): angular.IPromise<any> {
        if (!this.selectedMapEvent) {
            return;
        }

        const startPlotPromise = this.plotService.getPlot(this.selectedMapEvent.VehicleId, this.selectedMapEvent.StartPlotId);
        const endPlotPromise = this.plotService.getPlot(this.selectedMapEvent.VehicleId, this.selectedMapEvent.EndPlotId);

        return this.$q.all([startPlotPromise, endPlotPromise]).then(([startPlot, endPlot]) => {
            this.selectedMapEvent.startPlot = startPlot;
            this.selectedMapEvent.endPlot = endPlot;
        });
    }

    private processData(): void {
        this.eventCategories = [];
        this.driverEvents = [];
        this.locationEvents = [];

        this.filterEvents();

        this.filterDataByGroupFilter();

        this.events.forEach(event => {
            this.attachDriverAndVehicleToEvent(event);
            this.setEventTagIds(event);
            this.getEventCategories(event);
            this.getDriverEvents(event);
            this.getLocationEvents(event);
            this.getAccelerometerRecordForEvent(event);
        })


        this.createMarkers();

        this.sortEventCategories();
        this.processDriverEvents();
        this.processLocationEvents();
    }

    private filterEvents(): void {
        // Remove Harsh Steering and events with no StartLocationInformation
        if (!this.events?.length) {
            return;
        }
        this.events = this.events.filter(event => !!event.StartLocationInformation && event.EventName !== "Harsh Steering");
    }

    private filterDataByGroupFilter(): void {
        const filterByDriverIds = this.filters?.groupFilter?.group?.DriverIds?.length > 0;
        const filterByVehicleIds = this.filters?.groupFilter?.group?.VehicleIds?.length > 0;

        this.events = (filterByDriverIds || filterByVehicleIds) ? this.events.filter(event => {
            const isRelevantDriver: boolean = (this.filters.groupFilter.group.DriverIds.indexOf(event.DriverId) > -1);
            const isRelevantVehicle: boolean = (this.filters.groupFilter.group.VehicleIds.indexOf(event.VehicleId) > -1);

            return isRelevantDriver || isRelevantVehicle;
        }) : this.events;
    }

    attachDriverAndVehicleToEvent(event: IEvent): void {
        event.driver = this.driverService.findDriver(this.drivers, event.DriverId);
        event.vehicle = this.vehicleService.findVehicle(this.vehicles, event.VehicleId);
    }

    prepareDefaultView(): void {
        this.clearAllFilters();
        this.refreshAllMaps();
    }

    private setEventTagIds(event: IEvent) {
        event.tagIds = [];

        if (event.vehicle && event.vehicle.TagIds) {
            event.tagIds = event.tagIds.concat(event.vehicle.TagIds)
        }
    }

    private createPolylinesForEvent(event: IEvent, faded: boolean = false): IPolyline[] {
        if (!event) {
            return;
        }

        const polylines: IPolyline[] = [];

        if (event?.Type == ReasonCode.ContextualSpeeding && event?.markerModel.options?.position.lat) {

            const linePoints: number[][] = this.mapService.getPointsFromEncodedString(event.contextualSpeedingData.EncodedPolyline);
            const latLngArray: google.maps.LatLng[] = this.mapService.convertArrayToGoogleLatLng(linePoints);
            const polyline: IPolyline = this.mapService.createPolyline(event.Id, latLngArray, event.color.solidColour, event);

            polyline.options.icons = [{
                icon: {
                    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    fillOpacity: 1,
                    scale: 3,
                    fillColor: "white",
                    strokeColor: event.color.solidColour,
                    strokeWeight: 1,
                },
                offset: "100%",
                repeat: "100px"
            }];

            polylines.push(polyline);
        }

        return polylines;
    }

    private createMapMarkerForEvent(event: IEvent, latitude: number, longitude: number, label: string, icon: string): IGoogleMapsMarker {
        if (!event || !latitude || !longitude || !label || !icon) {
            return;
        }

        const markerOptions: any = this.googleMarkerService.createEventMarkerOptions({
            label: label,
            subIcon: icon,
            showLabel: true,
            position: { lat: latitude, lng: longitude }
        });

        const marker: IGoogleMapsMarker = {
            id: this.selectedMapEvent.Id,
            options: markerOptions
        };

        return marker;
    }

    private configureEventMarkerOptions(): void {
        this.eventMarkerOptions = {
            events: {},
            control: {},
            type: "cluster",
            typeOptions: {
                imagePath: "https://rawgit.com/googlemaps/js-marker-clusterer/gh-pages/images/m",
                maxZoom: 13,
                gridSize: 40 // default 60
            },
            typeEvents: {}
        };

        this.eventMarkerOptions.events.click = (marker, eventName, model, mouseEvent) => { this.onMarkerClick(marker, eventName, model, mouseEvent); };
        this.eventMarkerOptions.events.mouseover = (marker, eventName, model, mouseEvent) => { this.onMarkerMouseover(marker, eventName, model, mouseEvent); };
        this.eventMarkerOptions.typeEvents.postClusterShow = (clusterIcon) => { this.createClusterIcon(clusterIcon); }
    }

    private processSelectedEventInformation(): angular.IPromise<any> {

        if (!this.selectedMapEvent) {
            return this.$q.resolve();
        }

        this.loadingSelectedEventPlots = true;

        this.createRelatedEventsForSelectedMapEvent();
        this.clearSelectedEventVideoDownloadProgress();

        this.selectedEventVideos = []; // don't clear inside getVideosForEvent() otherwise interval refreshes will cause ng-repeat flashing, regardless of track by Id
        const eventVideoChannelsPromise = this.getVideosChannelsForEvent(this.selectedMapEvent);
        const eventFootageFunctionalityPromise = this.getRequestFootageFunctionalityAbilityFromEvent(this.selectedMapEvent);
        const eventPlotsPromise = this.updateSelectedEventPlots().then(() => {

            const durationFormatted = this.getFormattedDurationForSelectedEvent(this.selectedMapEvent, 'HH:mm:ss')

            this.selectedMapEvent.duration = `${durationFormatted}`;
            this.loadingSelectedEventPlots = false;
        });

        this.selectedMapEventVideoRequested = this.eventVideoHasBeenRequested(this.selectedMapEvent);

        return this.$q.all([eventVideoChannelsPromise, eventFootageFunctionalityPromise, eventPlotsPromise]);
    }

    getDurationForSelectedEvent(event: IEvent) {
        const startMoment: moment.Moment = this.veriDate.createMoment(event.StartTime);
        const endMoment: moment.Moment = this.veriDate.createMoment(event.EndTime);
        const duration: moment.Duration = moment.duration(endMoment.diff(startMoment));

        return duration;
    }

    getFormattedDurationForSelectedEvent(event: IEvent, format: string) {
        if (event.hasOwnProperty('EndTime') == false) {
            return "00:00:00";
        };

        const duration = this.getDurationForSelectedEvent(event);

        let durationFormatted = moment.utc(duration.asMilliseconds()).format(format);

        if (durationFormatted == "00:00:00") {
            switch (event.Type) {
                case ReasonCode.RolloverStabilityControl:
                    durationFormatted = "< 00:00:01";
                    break;
                case ReasonCode.ContextualSpeeding:
                    durationFormatted = "< 00:00:10";
                    break;
            }
        }

        return durationFormatted;
    }

    private createRelatedEventsForSelectedMapEvent(): void {
        this.eventsRelatedToSelectedMapEvent = [];

        const selectedEventRaisedMoment = moment(this.selectedMapEvent.StartTime);
        const selectedEventClearedMoment = moment(this.selectedMapEvent.EndTime);
        const earliestMoment = selectedEventRaisedMoment.subtract(1, 'minute');
        const latestMoment = selectedEventClearedMoment.add(1, 'minute');

        this.eventsRelatedToSelectedMapEvent = this.events.filter(event => {
            const eventRaisedMoment = moment(event.StartTime);

            return event !== this.selectedMapEvent
                && event?.Id !== this.selectedMapEvent?.Id
                && event?.DriverId === this.selectedMapEvent?.DriverId
                && event?.VehicleId === this.selectedMapEvent?.VehicleId
                && (eventRaisedMoment.isSameOrAfter(earliestMoment, "minute") && eventRaisedMoment.isSameOrBefore(latestMoment, "minute"))
        });
    }

    private createClusterIcon(clusterIcon: ClusterIcon): void {

        const markers = clusterIcon.cluster_.markers_;
        const eventArray: IEvent[] = markers.map(marker => marker.model);
        const matchingEventCategories = this.getEventCategoriesForEvents(eventArray);

        const data: IPieDataSeries[] = [];

        matchingEventCategories.forEach(category => {
            data.push({
                value: category.events.length,
                color: category.color.solidColour
            });
        });

        var pieCanvas = this.canvasService.getPieCanvas(clusterIcon.width_, data);
        clusterIcon.div_.appendChild(pieCanvas);
    }

    private updateOverviewBars(): void {
        this.updateContextualSpeedingOverviewBars();
        this.updateDriverEventsOverviewBars();
        this.updateLocationEventsOverviewBars();
    }

    private createOverviewBarFromEvents(id: string, label: string, events: IEvent[]): IEventBar {
        let newTotalEventsOverviewBar: IEventBar = {
            id: id,
            label: label,
            segments: [],
            total: events.length,
            maxTotal: events.length
        };

        newTotalEventsOverviewBar.segments = this.createSummaryBarSegmentsFromEvents(events);

        return newTotalEventsOverviewBar;
    }

    private updateContextualSpeedingOverviewBars(): void {
        this.updateContextualSpeedingRoadSpeedOverviewBars();
        this.updateContextualSpeedingRoadTypeOverviewBars();
        this.updateContextualSpeedingVehicleTypeOverviewBars();
    }

    private updateContextualSpeedingRoadSpeedOverviewBars(): void {
        this.contextualSpeedingRoadSpeedOverviewBars = [];

        let contextualSpeedingEvents: IEvent[] = (this.filters.eventCategory)
            ? this.events.filter(event => event.eventCategory === this.filters.eventCategory && event?.contextualSpeedingData)
            : this.events.filter(event => event?.contextualSpeedingData);

        let roadSpeeds: number[] = [];
        contextualSpeedingEvents.forEach(event => {
            if (roadSpeeds.indexOf(event?.contextualSpeedingData?.RoadSpeedKph) === -1) {
                roadSpeeds.push(event.contextualSpeedingData.RoadSpeedKph);
            }
        });

        contextualSpeedingEvents = this.filterEventsByDriverFilter(contextualSpeedingEvents);
        contextualSpeedingEvents = this.filterEventsByLocationFilter(contextualSpeedingEvents);

        const newEventBar: IEventBar = {
            id: `contextual-speeding-road-speed-0`,
            label: this.translationService.t("FleetEvents.SpeedLimit"),
            segments: [],
            total: contextualSpeedingEvents.length,
            maxTotal: contextualSpeedingEvents.length
        };

        newEventBar.segments = this.createContextualSpeedingRoadSpeedSummaryBarSegmentsFromEvents(contextualSpeedingEvents, roadSpeeds);
        newEventBar.maxTotal = newEventBar.total;

        this.contextualSpeedingRoadSpeedOverviewBars.push(newEventBar);
    }

    private updateContextualSpeedingRoadTypeOverviewBars(): void {
        this.contextualSpeedingRoadTypeOverviewBars = [];
        let highestEventsCount = 0;

        let contextualSpeedingEvents: IEvent[] = (this.filters.eventCategory)
            ? this.events.filter(event => event.eventCategory === this.filters.eventCategory && event?.contextualSpeedingData)
            : this.events.filter(event => event?.contextualSpeedingData);

        let roadTypes: string[] = [];
        contextualSpeedingEvents.forEach(event => {
            if (roadTypes.indexOf(event?.contextualSpeedingData?.RoadType) === -1) {
                roadTypes.push(event.contextualSpeedingData.RoadType);
            }
        });

        contextualSpeedingEvents = this.filterEventsByDriverFilter(contextualSpeedingEvents);
        contextualSpeedingEvents = this.filterEventsByLocationFilter(contextualSpeedingEvents);

        roadTypes.sort((roadType1, roadType2) => (roadType1 > roadType2) ? 1 : -1);

        let roadTypesIndex = -1;
        roadTypes.forEach(roadType => {
            roadTypesIndex++;

            const baseLabel = this.translationService.t(`FleetEvents.RoadType${roadType}`);

            const newEventBar: IEventBar = {
                id: `contextual-speeding-road-type-${roadTypesIndex}`,
                label: baseLabel,
                segments: [],
                total: contextualSpeedingEvents.length,
                maxTotal: contextualSpeedingEvents.length
            };

            const newBarSegment: IEventBarSegment<IEvent> = {
                id: roadTypesIndex,
                label: "",
                total: 0,
                color: '',
                events: []
            }

            contextualSpeedingEvents.forEach(event => {
                if (event?.contextualSpeedingData?.RoadType === roadType) {
                    newBarSegment.total++;
                    newBarSegment.color = "#b9b9b9"; // this.colourService.getVigetColor((roadTypesIndex + 0) * 2);
                    newBarSegment.events.push(event);
                }
            });

            newBarSegment.label = `${newBarSegment.total} x ${baseLabel}`;

            newEventBar.segments.push(newBarSegment);
            newEventBar.total = newEventBar.segments[0].events.length;

            highestEventsCount = Math.max(highestEventsCount, newEventBar.total);
            this.contextualSpeedingRoadTypeOverviewBars.push(newEventBar);
        });

        this.contextualSpeedingRoadTypeOverviewBars.forEach(bar => bar.maxTotal = highestEventsCount);
        this.contextualSpeedingRoadTypeOverviewBars.sort((bar1, bar2) => { return bar2.total - bar1.total });
    }

    private updateContextualSpeedingVehicleTypeOverviewBars(): void {
        this.contextualSpeedingVehicleTypeOverviewBars = [];
        let highestEventsCount = 0;

        let contextualSpeedingEvents: IEvent[] = (this.filters.eventCategory)
            ? this.events.filter(event => event.eventCategory === this.filters.eventCategory && event?.contextualSpeedingData)
            : this.events.filter(event => event?.contextualSpeedingData);

        let vehicleTypes: string[] = [];
        contextualSpeedingEvents.forEach(event => {
            if (!VehicleType[event?.vehicle?.VehicleType]) {
                return;
            }

            const vehicleTypeString: string = VehicleType[event.vehicle.VehicleType];

            if (vehicleTypes.indexOf(vehicleTypeString) === -1) {
                vehicleTypes.push(vehicleTypeString);
            }
        });

        contextualSpeedingEvents = this.filterEventsByDriverFilter(contextualSpeedingEvents);
        contextualSpeedingEvents = this.filterEventsByLocationFilter(contextualSpeedingEvents);

        vehicleTypes.sort((vehicleType1, vehicleType2) => (vehicleType1 > vehicleType2) ? 1 : -1);

        let vehicleTypesIndex = -1;

        vehicleTypes.forEach(vehicleType => {
            vehicleTypesIndex++;

            const baseLabel = this.translationService.t(`VehicleTypeEnum.${vehicleType}`);

            const newEventBar: IEventBar = {
                id: `contextual-speeding-vehicle-type-${vehicleTypesIndex}`,
                label: baseLabel,
                segments: [],
                total: contextualSpeedingEvents.length,
                maxTotal: contextualSpeedingEvents.length
            };

            const newBarSegment: IEventBarSegment<IEvent> = {
                id: vehicleTypesIndex,
                label: "",
                total: 0,
                color: '',
                events: []
            };

            contextualSpeedingEvents.forEach(event => {
                if (!VehicleType[event?.vehicle?.VehicleType]) {
                    return;
                }

                if (VehicleType[event.vehicle.VehicleType] === vehicleType) {
                    newBarSegment.total++;
                    newBarSegment.color = "#b9b9b9";
                    newBarSegment.events.push(event);
                }
            });

            newBarSegment.label = `${newBarSegment.total} x ${baseLabel}`;

            newEventBar.segments.push(newBarSegment);
            newEventBar.total = newEventBar.segments[0].events.length;

            highestEventsCount = Math.max(highestEventsCount, newEventBar.total);
            this.contextualSpeedingVehicleTypeOverviewBars.push(newEventBar);
        });

        this.contextualSpeedingVehicleTypeOverviewBars.forEach(bar => bar.maxTotal = highestEventsCount);
        this.contextualSpeedingVehicleTypeOverviewBars.sort((bar1, bar2) => { return bar2.total - bar1.total });
    }

    private updateDriverEventsOverviewBars(): void {
        let highestEventsCount = 0;

        this.driverEventsOverviewBars = [];
        this.driverEvents.forEach(driverEvent => {

            if (driverEvent?.driver?.DisplayName === this.translationService.t("FleetEvents.AllDrivers")) {
                // don't add non-existent driver entry or the placeholder event into the overview bars
                return;
            }

            let barEvents: IEvent[] = driverEvent.events;
            barEvents = this.filterEventsByCategoryFilter(barEvents);
            barEvents = this.filterEventsByLocationFilter(barEvents);

            const driverId: string = `${driverEvent?.driver?.Id}` || `unknown`;
            const driverName: string = driverEvent?.driver?.DisplayName || this.translationService.t("FleetEvents.UnknownDriver");

            const newBar: IEventBar = this.createOverviewBarFromEvents(`driver-${driverId}`, driverName, barEvents);

            newBar.dataId = driverId;
            highestEventsCount = Math.max(highestEventsCount, newBar.total);
            this.driverEventsOverviewBars.push(newBar);
            driverEvent.overviewBar = newBar;
        });

        this.driverEventsOverviewBars.forEach(bar => bar.maxTotal = highestEventsCount);
        this.driverEventsOverviewBars.sort((bar1, bar2) => { return bar2.total - bar1.total });
    }

    private updateLocationEventsOverviewBars(): void {
        this.locationEventsOverviewBars = [];
        let highestEventsCount = 0;

        this.locationEvents.forEach(locationEvent => {
            if (locationEvent?.locationIdentifier === this.translationService.t("FleetEvents.AllLocations")) {
                // don't add the placeholder event into the overview bars
                return;
            }

            const label: string = locationEvent.locationIdentifier || this.translationService.t("FleetEvents.UnknownLocation");
            let barEvents: IEvent[] = locationEvent.events;
            barEvents = this.filterEventsByCategoryFilter(barEvents);
            barEvents = this.filterEventsByDriverFilter(barEvents);

            const newBar: IEventBar = this.createOverviewBarFromEvents(`location-${locationEvent.locationIdentifier}`, label, barEvents);

            newBar.dataId = locationEvent.locationIdentifier;
            highestEventsCount = Math.max(highestEventsCount, newBar.total);
            this.locationEventsOverviewBars.push(newBar);
            locationEvent.overviewBar = newBar;
        });

        this.locationEventsOverviewBars.forEach(b => b.maxTotal = highestEventsCount);
        this.locationEventsOverviewBars.sort((bar1, bar2) => { return bar2.total - bar1.total });
    }

    private filterEventsByCategoryFilter(events: IEvent[]): IEvent[] {
        if (!this.filters.eventCategory) {
            return events;
        }

        return events.filter(event => event.eventCategory === this.filters.eventCategory);
    }

    private filterEventsByDriverFilter(events: IEvent[]): IEvent[] {
        if (!this.filters.driverEvent) {
            return events;
        }

        return events.filter(event => event.driver === this.filters.driverEvent.driver);
    }

    private filterEventsByLocationFilter(events: IEvent[]): IEvent[] {
        if (!this.filters.locationEvent?.locationIdentifier) {
            return events;
        }

        return events.filter(event => {
            const eventLocationIdentifier: string = this.getLocationIdentifier(event.StartLocationInformation);

            return eventLocationIdentifier === this.filters.locationEvent.locationIdentifier;
        });
    }

    private createContextualSpeedingRoadSpeedSummaryBarSegmentsFromEvents(events: IEvent[], roadSpeeds: number[]): IEventBarSegment<IEvent>[] {
        const barSegments: IEventBarSegment<IEvent>[] = [];

        roadSpeeds.sort((roadSpeed1, roadSpeed2) => roadSpeed1 - roadSpeed2);

        let currentRoadSpeedIndex = -1;
        roadSpeeds.forEach(roadSpeed => {
            currentRoadSpeedIndex++;

            const baseLabel = `${roadSpeed}Kph`;
            const newBarSegment: IEventBarSegment<IEvent> = {
                id: currentRoadSpeedIndex,
                label: baseLabel,
                total: 0,
                color: '',
                events: []
            }

            events.forEach(event => {
                if (event?.contextualSpeedingData?.RoadSpeedKph === roadSpeed) {
                    newBarSegment.total++;
                    newBarSegment.color = this.colourService.getVigetColor((currentRoadSpeedIndex + 0) * 2);
                    newBarSegment.events.push(event);
                }
            });

            const speedUnitSuffix = this.unitConversionService.getConversionLabel(UnitConversionType.Speed);
            const speedUnit = this.unitConversionService.convertToUserUnit(roadSpeed, UnitConversionType.Speed);

            newBarSegment.label = `${newBarSegment.total} x ${speedUnit} ${speedUnitSuffix}`;

            barSegments.push(newBarSegment);
        });

        return barSegments;
    }

    private createSummaryBarSegmentsFromEvents(events: IEvent[]): IEventBarSegment<IEvent>[] {
        const barSegments: IEventBarSegment<IEvent>[] = [];

        this.eventCategories.forEach(eventCategory => {
            const baseLabel = `${eventCategory.name}`;
            const newBarSegment: IEventBarSegment<IEvent> = {
                id: eventCategory.id,
                label: baseLabel,
                total: 0,
                color: '',
                events: []
            }

            events.forEach(event => {
                if (event.eventCategory === eventCategory) {
                    newBarSegment.total++;
                    newBarSegment.color = eventCategory.color.solidColour;
                    newBarSegment.events.push(event);
                }
            });

            newBarSegment.label = `${newBarSegment.total} x ${baseLabel}`;
            barSegments.push(newBarSegment);
        });

        return barSegments;
    }

    private createMarkers(): void {
        var vehicleList = [];
        const pipCache: IGoogleMapsMarkerOptions[] = [];

        this.events.forEach(event => {
            if (!pipCache[event.color.solidColour]) {
                pipCache[event.color.solidColour] = this.generateEventMarkerPipOptions(event);
            }

            if (!event.markerModel) {
                event.markerModel = {
                    id: event.Id,
                    options: copy(pipCache[event.color.solidColour])
                };

                event.markerModel.options.position = { lat: event.StartLatitude, lng: event.StartLongitude };
                event.markerModel.options.title = event.EventName;
            }

            if (vehicleList[event.VehicleId]) {
                vehicleList[event.VehicleId]++;
            }
            else {
                vehicleList[event.VehicleId] = 1;
            }
        });
    }

    private generateEventMarkerPipOptions(event: IEvent): IGoogleMapsMarkerOptions {
        let options: IGoogleMapsMarkerOptions = {
            isPip: true,
        };

        const width = 16
        const icon: google.maps.Icon = {
            url: this.veriMarkerService.generateColorPip(event.color.solidColour, width),
            scaledSize: new google.maps.Size(width, width),
            anchor: new google.maps.Point(width / 2, width / 2),
        }

        options = {
            icon: icon,
            zIndex: 0
        };

        return options;
    }

    private fitMapToMapEvent(veriMap: IVeriMapComponent, event: IEvent, withSmoothZoom: boolean = false): void {
        if (!veriMap) {
            return;
        }

        if (!event || !event?.StartLatitude || !event.StartLongitude) {
            this.fitMapToDisplayedMapEvents(veriMap, this.displayedEvents);
            return;
        }

        let googleMapsBounds: google.maps.LatLng[] = [];
        const eventMapBounds: google.maps.LatLng = new google.maps.LatLng(event.StartLatitude, event.StartLongitude);

        // get latlongs based on entire path of contextual speeding event
        if (event?.Type == ReasonCode.ContextualSpeeding && event?.markerModel.options?.position.lat && event.markerModel.options.position.lng) {

            const linePoints: number[][] = this.mapService.getPointsFromEncodedString(event.contextualSpeedingData.EncodedPolyline);
            googleMapsBounds = this.mapService.convertArrayToGoogleLatLng(linePoints);
        }

        if (googleMapsBounds.length === 0) {
            googleMapsBounds.push(new google.maps.LatLng(event.StartLatitude, event.StartLongitude));
        }

        if (withSmoothZoom) {
            this.mapService.smoothPanAndZoomTo(veriMap.getMap(), eventMapBounds, 16);
        }
        else {
            veriMap.fitMap(googleMapsBounds);
        }
    }

    private fitMapToDisplayedMapEvents(veriMap: IVeriMapComponent, events: IEvent[]): void {
        if (!veriMap || !events) {
            return;
        }

        const googleMapsBounds: google.maps.LatLng[] = [];

        events.forEach(event => {
            googleMapsBounds.push(new google.maps.LatLng(event.StartLatitude, event.StartLongitude));
        });

        if (veriMap) {
            veriMap.fitMap(googleMapsBounds);
        }
    }

    private processDisplayedView(): void {
        this.clearCurrentSelectedEvent();

        this.filterDisplayedEvents();
        this.updateOverviewBars();
        this.refreshAllMaps();
        this.postProcessFilteredEvents(); // creates a synthetic total in categories / drivers / locations, based on applied filters
        this.createDataViewTableRows();
    }

    private filterDisplayedEvents(): void {
        let preparedEvents: IEvent[] = [];

        for (var i = 0; i < this.events.length; i++) {
            const event = this.events[i];
            const eventLocationIdentifier: string = this.getLocationIdentifier(event.StartLocationInformation);

            const noDriverExistsOnEvent = !event?.driver;
            const noCategoryFilterOrFilterMatches = !this.filters?.eventCategory || (event?.eventCategory === this.filters?.eventCategory);
            const noDriverFilterOrFilterMatches = !this.filters?.driverEvent || (event?.driver?.Id === this.filters?.driverEvent?.driver?.Id || (noDriverExistsOnEvent && (this.filters?.driverEvent && !this.filters?.driverEvent?.driver)));
            const noLocationFilterOrFilterMatches = !this.filters?.locationEvent || (eventLocationIdentifier === this.filters?.locationEvent?.locationIdentifier);

            if (noCategoryFilterOrFilterMatches &&
                noDriverFilterOrFilterMatches &&
                noLocationFilterOrFilterMatches) {
                preparedEvents.push(event);
            }
        }

        this.displayedEvents = preparedEvents;
    }

    private getEventCategoriesForEvents(events: IEvent[]): IEventCategory[] {
        const categories: IEventCategory[] = [];

        events.forEach(event => {
            if (categories.filter(category => category == event.eventCategory).length == 0) {
                categories.push(event.eventCategory);
            }
        });

        return categories;
    }

    private getEventCategories(event: IEvent): void {

        let existingCategory: IEventCategory = this.eventCategories.find(eventCategory => eventCategory.name === event.EventName);

        if (!existingCategory) {
            existingCategory = {
                id: event.Type,
                name: event.EventName,
                count: 0,
                events: [],
                color: this.colourService.getColour(this.eventCategories.length, true)
            }

            this.eventCategories.push(existingCategory);
        }

        event.eventCategory = existingCategory;
        event.color = existingCategory.color;

        existingCategory.events.push(event);
        existingCategory.count = existingCategory.events.length;


    }

    private sortEventCategories(): void {

        this.eventCategories.sort((x, y) => x.name === this.translationService.t("FleetEvents.AllCategories") ? 0 : x.name > y.name ? 1 : -1);

        this.eventCategories.forEach((category, i) => {
            if (!category.color) {
                return;
            }

            category.color.solidColour = this.colourService.getVigetColor((i + 0) * 2);
        });

        if (this.eventCategories?.length > 0) {
            this.eventCategories.unshift({
                id: -1,
                name: this.translationService.t("FleetEvents.AllCategories"),
                count: 0,
                events: []
            });
        }
    }

    private getDriverEvents(event: IEvent): void {

        const isUnknownDriver: boolean = !event?.DriverId || !event.driver;
        let driverEvent: IFleetDriverEvent = this.driverEvents.find(driverEvent => driverEvent?.driver?.Id === event.DriverId || (isUnknownDriver && driverEvent.isUnknownDriver));
        let driver: IDriver = this.driverService.findDriver(this.drivers, event.DriverId);

        if (!driverEvent) {
            driverEvent = {
                driver,
                events: [],
                isUnknownDriver: isUnknownDriver
            };

            this.driverEvents.push(driverEvent);
            driverEvent.repeatId = this.driverEvents.length;
            driverEvent.total = this.driverEvents.length;
        }

        driverEvent.events.push(event);


    }

    private processDriverEvents(): void {

        if (this.driverEvents?.length > 0) {
            this.driverEvents.sort((driverEvent1, driverEvent2) => driverEvent2.events.length - driverEvent1.events.length);

            this.driverEvents.unshift({
                driver: {
                    DisplayName: this.translationService.t("FleetEvents.AllDrivers")
                },
                events: []
            });
        }
    }

    private getLocationEvents(event: IEvent): void {

        const cityPostcode: ICityPostcode = this.getCityPostcodeFromLocation(event.StartLocationInformation);
        const locationIdentifier = this.getLocationIdentifier(event.StartLocationInformation);

        let locationEvent: IFleetLocationEvent = this.locationEvents.find(locationEvent => locationEvent.locationIdentifier === locationIdentifier);

        if (!locationEvent) {
            locationEvent = {
                locationIdentifier,
                postcode: cityPostcode?.postcode,
                events: []
            };

            this.locationEvents.push(locationEvent);
            locationEvent.repeatId = this.locationEvents.length;
            locationEvent.total = this.locationEvents.length;
        }

        locationEvent.events.push(event);

    }

    private processLocationEvents(): void {
        if (this.locationEvents?.length > 0) {
            this.locationEvents.sort((locationEvent1, locationEvent2) => locationEvent2.events.length - locationEvent1.events.length);

            this.locationEvents.unshift({
                locationIdentifier: this.translationService.t("FleetEvents.AllLocations"),
                postcode: null,
                events: []
            });
        }
    }

    private getAccelerometerRecordForEvent(event: IEvent): void {
        event.accelerometerRecord = this.accelerometerEventsMap.get(event.StartPlotId);
    }

    private postProcessFilteredEvents(): void {
        this.eventCategories.forEach(eventCategory => eventCategory.count = eventCategory.events.length);
        this.driverEvents.forEach(driverEvent => driverEvent.total = driverEvent.events.length);
        this.locationEvents.forEach(locationEvent => locationEvent.total = locationEvent.events.length);

        // filter total for driver events
        this.eventCategories.forEach(eventCategory => {
            eventCategory.count = eventCategory.events.filter(event =>
                (!this.filters?.locationEvent || this.filters?.locationEvent?.events?.indexOf(event) > -1) &&
                (!this.filters?.driverEvent || this.filters?.driverEvent?.events?.indexOf(event) > -1)
            ).length;
        });

        // filter total for driver events
        this.driverEvents.forEach(driverEvent => {
            driverEvent.total = driverEvent.events.filter(event =>
                (!this.filters?.eventCategory || this.filters?.eventCategory?.events?.indexOf(event) > -1) &&
                (!this.filters?.locationEvent || this.filters?.locationEvent?.events?.indexOf(event) > -1)
            ).length;
        });

        // filter total for location events
        this.locationEvents.forEach(locationEvent => {
            locationEvent.total = locationEvent.events.filter(event =>
                (!this.filters?.eventCategory || this.filters?.eventCategory?.events?.indexOf(event) > -1) &&
                (!this.filters?.driverEvent || this.filters?.driverEvent?.events?.indexOf(event) > -1)
            ).length;
        });

        if (this.driverEvents?.length > 0) {
            this.driverEvents.sort((event1, event2) => event2.total - event1.total);
            this.moveAllDriversToTop();
        }

        if (this.locationEvents?.length > 0) {
            this.locationEvents.sort((event1, event2) => event2.total - event1.total);
            this.moveAllLocationsToTop();
        }

        this.addGrandTotals();
    }

    private moveAllDriversToTop(): void {
        const allDriversIndex = this.driverEvents.map(event => event?.driver?.DisplayName).indexOf(this.translationService.t("FleetEvents.AllDrivers"));

        if (allDriversIndex) {
            this.driverEvents.unshift(this.driverEvents.splice(allDriversIndex, 1)[0]);
        }
    }

    private moveAllLocationsToTop(): void {
        const allLocationsIndex = this.locationEvents.map(event => event.locationIdentifier).indexOf(this.translationService.t("FleetEvents.AllLocations"));

        if (allLocationsIndex) {
            this.locationEvents.unshift(this.locationEvents.splice(allLocationsIndex, 1)[0]);
        }
    }

    private addGrandTotals(): void {
        this.eventCategories?.forEach((eventCategory, index) => {
            if (index > 0) {
                this.eventCategories[0].count = this.eventCategories[0].count + eventCategory.count;
            }
        });

        this.driverEvents?.forEach((driverEvent, index) => {
            if (index > 0) {
                this.driverEvents[0].total = this.driverEvents[0].total + driverEvent.total;
            }
        });

        this.locationEvents?.forEach((locationEvent, index) => {
            if (index > 0) {
                this.locationEvents[0].total = this.locationEvents[0].total + locationEvent.total;
            }
        });
    }

    private splitPascalCase(input: string): string {
        return (!!input) ? input.replace(/([A-Z][a-z])/g, " $1").replace(/(\d)/g, " $1").trim() : "";
    }

    private configureDataViewTable(): void {
        this.createTable();
    }


    private createTable(): void {
        this.vTableColumns = [
            {
                header: this.vTableService.createDateTableHeader(),
                createCellFunction: event => this.vTableService.createDateCell(event.StartTime)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: event => this.vTableService.createVehicleRegCell(event.vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Driver" }),
                createCellFunction: event => this.vTableService.createTableCell(`${(event?.driver?.DisplayName) ? event.driver.DisplayName : this.translationService.t("FleetEvents.UnknownDriver")}`)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Category" }),
                createCellFunction: event => this.vTableService.createTableCell(event?.eventCategory?.name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Duration", sortable: true, sortType: SortType.Number }),
                createCellFunction: event => this.vTableService.createTableCell(this.getFormattedDurationForSelectedEvent(event, 'HH:mm:ss'), { sortData: this.getDurationForSelectedEvent(event).asMilliseconds() })
            },
            {
                header: this.vTableService.createLocationInformationHeader(),
                createCellFunction: event => {
                    let location: string = event.StartLocationInformation.LocationDescription;

                    if (event.StartLocationInformation.geofenceNames) {
                        location = `(${event.StartLocationInformation.geofenceNames})`
                    }

                    return this.vTableService.createTableCell(location)
                }
            }
        ]
    }

    private createVTableRows(): void {
        this.vTableRows = this.vTableService.createTableRows(this.displayedEvents, this.vTableColumns);
    }

    private createDataViewTableRows() {
        this.createVTableRows();
    }

    private eventVideoHasBeenRequested(event: IEvent): boolean {
        return this.requestedVideoEventIds.indexOf(event?.Id) > -1;
    }

    private checkForEventVideos(fromRequestingVideo: boolean = false): void {
        if (!this.userHasVideoAccess) {
            return;
        }

        this.cancelExistingEventVideoInterval();
        this.getVideosForEvent(this.selectedMapEvent);
        this.gatherEventVideosInterval = this.$interval(() => {
            if ((fromRequestingVideo && this.selectedEventVideos?.length < 1) || (this.selectedEventVideoDownloadProgress > -1 && this.selectedEventVideoDownloadStatus)) {
                this.getVideosForEvent(this.selectedMapEvent);
            } else {
                this.cancelExistingEventVideoInterval();
            }
        }, 10_000);
    }

    private clearSelectedEventVideoDownloadProgress(): void {
        this.selectedEventVideoDownloadProgress = -1;
        this.selectedEventVideoDownloadStatus = null;
    }

    private cancelExistingEventVideoInterval(): void {
        if (this.gatherEventVideosInterval) {
            this.$interval.cancel(this.gatherEventVideosInterval);
        }
    }

    private getLocationIdentifier(locationInformation: ILocationInformation): string {
        const cityPostcode: ICityPostcode = this.getCityPostcodeFromLocation(locationInformation);

        if (!locationInformation.geofenceNames?.length) {
            return cityPostcode?.city;
        }

        return `(${locationInformation.geofenceNames})`;
    }

    getCityPostcodeFromLocation(locationInformation: ILocationInformation): ICityPostcode {
        if (!locationInformation?.LocationDescription) {
            return;
        }

        let cityPostcode: ICityPostcode = {};

        const locationSegments: string[] = locationInformation.LocationDescription.split(",");

        if (!locationSegments?.length) {
            return;
        }

        // example: "(Avara Foods Ltd  Brackey Depot NN13 7EX) "
        if (locationSegments.length === 1) {
            const simpleValue = locationSegments[0].trim();

            return { city: simpleValue, postcode: simpleValue };
        }

        const cityAndPostcode = locationSegments[locationSegments.length - 2].trim();
        const cityAndPostcodeSegments: string[] = cityAndPostcode.split(" ");

        // example: "Stockton-on-Tees"
        if (cityAndPostcodeSegments.length === 1) {
            const simpleValue = cityAndPostcodeSegments[0].trim();

            return { city: simpleValue, postcode: simpleValue };

            // example: "London E14" or "CR5X+8R Mitcham" or "Northallerton DL6"
        } else if (cityAndPostcodeSegments.length === 2) {
            cityPostcode.city = cityAndPostcodeSegments.find(locationString => locationString.indexOf("+") === -1).trim();
            const additionPostcode = cityAndPostcodeSegments.find(locationString => locationString.indexOf("+") > -1);

            // example: "CR5X+8R Mitcham" or "GGPW+3J Bolton"
            if (additionPostcode) {
                cityPostcode.postcode = additionPostcode.replace("+", " ");

                return cityPostcode;
            }

            cityPostcode.postcode = cityAndPostcodeSegments[1].trim()

            return cityPostcode;

            // example: "London W3 6YG" or "Northampton NN4 5DD"
        } else if (cityAndPostcodeSegments.length === 3) {
            cityPostcode.city = cityAndPostcodeSegments.filter((segment, index) => index < 1).join(" ");;
            cityPostcode.postcode = cityAndPostcodeSegments.filter((segment, index) => index > 0).join(" ");

            return cityPostcode;
        }

        return {
            city: locationInformation.LocationDescription, postcode: locationInformation.LocationDescription
        };
    }
}

export class FleetEventsComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            filteredDriverId: "<",
            reportDefinition: "<"
        };

        this.controller = FleetEventsComponentController;
        this.template = require("./fleetEvents.component.html");
    }
}
